import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchDashboardStats } from '@/lib/api'

export default function Dashboard() {
  const [stats, setStats] = useState({ totalBusinesses: 0, activeBusinesses: 0, totalEmirates: 0, totalReviews: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchDashboardStats().then(s => { setStats(s); setLoading(false) }) }, [])

  const items = [
    { label: 'Total Centers', value: stats.totalBusinesses, trend: '+12%', up: true, color: 'from-indigo-500 to-indigo-600', icon: 'M3 21h18M4 3h14M5 3v18m12-18v18M8 7h2m-2 3h2m-2 3h2m4-6h2m-2 3h2m-2 3h2' },
    { label: 'Active Now', value: stats.activeBusinesses, trend: `${stats.totalBusinesses > 0 ? Math.round(stats.activeBusinesses / stats.totalBusinesses * 100) : 0}%`, up: true, color: 'from-emerald-500 to-emerald-600', icon: 'M4.5 12.75l6 6 9-13.5' },
    { label: 'Total Reviews', value: stats.totalReviews, trend: '4.3 avg', up: true, color: 'from-amber-500 to-amber-600', icon: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z' },
    { label: 'Emirates', value: stats.totalEmirates, trend: 'All active', up: true, color: 'from-purple-500 to-purple-600', icon: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zM2 12h20M12 2a15 15 0 014 10 15 15 0 01-4 10 15 15 0 01-4-10 15 15 0 014-10z' },
  ]

  return (
    <Layout title="Dashboard">
      <div className="max-w-7xl mx-auto space-y-7">
        {/* Hero card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-brand-950 p-8 md:p-10">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 800 200"><defs><pattern id="dash-grid" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M24 0L0 0 0 24" fill="none" stroke="white" strokeWidth="0.5"/></pattern></defs><rect width="800" height="200" fill="url(#dash-grid)"/></svg>
          </div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-medium text-brand-200 mb-4 border border-white/5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                All systems operational
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">TypingCenterNear.me</h2>
              <p className="text-brand-200/70 mt-2 text-sm max-w-md">Complete directory of typing centers across the UAE. Manage listings, services, and emirates from one place.</p>
            </div>
            <div className="flex gap-3">
              <a href="/businesses/new" className="inline-flex items-center gap-2 bg-white text-slate-900 text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-brand-50 transition-colors shadow-lg shadow-white/5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add Business
              </a>
              <a href="/businesses" className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-5 py-2.5 rounded-xl hover:bg-white/15 transition-colors border border-white/10">
                View All
              </a>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(s => (
            <div key={s.label} className="group bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all duration-300 p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl opacity-[0.07] group-hover:opacity-[0.12] transition-opacity rounded-bl-3xl" style={{ backgroundImage: `linear-gradient(to bottom left, var(--tw-gradient-from), transparent)` }} />
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-lg shadow-current/20`}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d={s.icon}/></svg>
                </div>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${s.up ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50'} px-2 py-0.5 rounded-full`}>
                  {s.up && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 15l-6-6-6 6"/></svg>}
                  {s.trend}
                </span>
              </div>
              {loading ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-8 w-16 bg-surface-100 rounded" />
                  <div className="h-3 w-20 bg-surface-100 rounded" />
                </div>
              ) : (
                <>
                  <p className="text-3xl font-bold text-slate-900 tracking-tight">{s.value.toLocaleString()}</p>
                  <p className="text-[13px] text-slate-500 mt-1 font-medium">{s.label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Quick actions + Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-5 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { href: '/businesses', label: 'All Businesses', desc: 'View & manage', icon: 'M3 21h18M4 3h14' },
                { href: '/businesses/new', label: 'New Business', desc: 'Add listing', icon: 'M12 5v14M5 12h14' },
                { href: '/services', label: 'Services', desc: 'Edit catalog', icon: 'M4.5 12.75l6 6 9-13.5' },
                { href: '/emirates', label: 'Emirates', desc: 'Regions', icon: 'M12 2a10 10 0 1010 10A10 10 0 0012 2z' },
              ].map(qa => (
                <a key={qa.href} href={qa.href} className="flex items-start gap-3 p-4 rounded-xl border border-surface-200 hover:border-brand-200 hover:bg-brand-50/50 transition-all duration-200 group">
                  <div className="w-9 h-9 rounded-lg bg-surface-100 group-hover:bg-brand-100 flex items-center justify-center flex-shrink-0 transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="text-slate-500 group-hover:text-brand-600"><path d={qa.icon}/></svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 group-hover:text-brand-700 transition-colors">{qa.label}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{qa.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-5 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              System Info
            </h3>
            <div className="space-y-1">
              {[
                { label: 'Database', value: 'Supabase PostgreSQL' },
                { label: 'Region', value: 'eu-west-1 (Ireland)' },
                { label: 'Frontend', value: 'React 19 + Vite 6' },
                { label: 'Auth', value: 'Supabase Auth' },
                { label: 'Site', value: 'typingcenternear.me', href: 'https://typingcenternear.me' },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-surface-50 transition-colors">
                  <span className="text-[13px] text-slate-500 font-medium">{row.label}</span>
                  {row.href ? (
                    <a href={row.href} target="_blank" rel="noopener noreferrer" className="text-[13px] font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1">
                      {row.value}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
                    </a>
                  ) : (
                    <span className="text-[13px] font-medium text-slate-700">{row.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
